import type { SupabaseClient } from '@supabase/supabase-js'
import { supabaseClient } from './client'
import { allFileUploadSelectFields } from './select'
import { getFileUrl } from './file-url'
import { FileUploadCategoryChoices, FileUploadSubCategoryChoices } from './choices'
import { getContentTypeId, type ContentTypeKey } from './content-types'

export type { ContentTypeKey } from './content-types'
export { getContentTypeId } from './content-types'

/**
 * Maps content type models to their dedicated FK column on file_fileupload.
 * The table enforces a `fileupload_single_parent` constraint: only one of these
 * FK columns can be populated at a time (space_id, venue_id,
 * event_space_list_membership_id, event_email_id).
 *
 * Models not listed here fall back to filtering by `object_id`.
 */
const FK_COLUMN_MAP: Partial<Record<ContentTypeKey, string>> = {
  'main:venue': 'venue_id',
  'main:space': 'space_id',
  'crm:eventspacelistmembership': 'event_space_list_membership_id',
  'crm:eventemail': 'event_email_id',
}

export interface TransformedFileUpload {
  id: number
  update_author: number | null
  url: string
  filename: string | null
  mime_type: string | null
  category: string
  category_label: string
  sub_category: string[]
  sub_category_label: string
  caption: string | null
  created_at: string | null
  updated_at: string | null
  upload: string
}

/**
 * Fetches file uploads from file_fileupload table.
 * Only returns rows where the `upload` field is non-null and non-empty.
 *
 * @param objectIds - The object_id values to filter by
 * @param categories - The file category strings to filter by
 * @param model - "app_label:model" key to filter by content_type_id.
 *   Required because object_id alone is ambiguous across content types.
 * @param client - Optional Supabase client override
 * @param options - Optional abort signal
 */
export async function fetchFileUploads(objectIds: number[], categories: string[], model: ContentTypeKey, client?: SupabaseClient, options?: { signal?: AbortSignal }): Promise<any[]> {
  if (!objectIds.length) return []
  const db = client ?? supabaseClient
  const fkColumn = FK_COLUMN_MAP[model]
  const contentTypeId = getContentTypeId(model)
  const idList = objectIds.join(',')

  let query = db.from('file_fileupload').select(allFileUploadSelectFields).in('category', categories).not('upload', 'is', null).neq('upload', '').is('deleted_at', null)

  if (fkColumn) {
    // Match rows that have the FK populated OR rows matched by content_type_id + object_id
    query = query.or(`${fkColumn}.in.(${idList}),and(content_type_id.eq.${contentTypeId},object_id.in.(${idList}))`)
  } else {
    query = query.eq('content_type_id', contentTypeId).in('object_id', objectIds)
  }

  if (options?.signal) {
    query = (query as any).abortSignal(options.signal)
  }

  const { data, error } = await query
  if (error) {
    console.error('[fetchFileUploads] Error:', error)
    throw error
  }
  return data || []
}

/**
 * Fetches file uploads for multiple content types in parallel.
 * Each content type gets its own query to ensure correct object_id scoping.
 *
 * @param queries - Array of { objectIds, categories, model } to fetch in parallel
 * @param client - Optional Supabase client override
 * @param options - Optional abort signal
 * @returns Combined array of all file upload rows
 */
export async function fetchFileUploadsMulti(
  queries: Array<{ objectIds: number[]; categories: string[]; model: ContentTypeKey }>,
  client?: SupabaseClient,
  options?: { signal?: AbortSignal }
): Promise<any[]> {
  const results = await Promise.all(queries.map((q) => fetchFileUploads(q.objectIds, q.categories, q.model, client, options)))
  return results.flat()
}

/**
 * Groups file uploads by object_id into a Map.
 */
export function groupFilesByObjectId(files: any[]): Map<number, any[]> {
  const map = new Map<number, any[]>()
  for (const f of files) {
    const key = f.object_id as number
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(f)
  }
  return map
}

/**
 * Transforms a raw file_fileupload row into a standardized object.
 * URL is always derived from the `upload` field via the resize worker — never from the `url` column.
 */
export function transformFileUpload(file: any): TransformedFileUpload {
  const url = getFileUrl(file.upload, file.filename)

  return {
    id: file.id,
    update_author: file.update_author_id,
    url,
    filename: file.filename,
    mime_type: file.mime_type || null,
    category: file.category,
    category_label: FileUploadCategoryChoices[file.category] || file.category || '',
    sub_category: file.sub_category || [],
    sub_category_label: Array.isArray(file.sub_category) ? file.sub_category.map((s: string) => FileUploadSubCategoryChoices[s] || s).join(', ') : '',
    caption: file.caption,
    created_at: file.created_at,
    updated_at: file.updated_at,
    upload: file.upload,
  }
}
